import React from 'react'
import { api, basicAuth, loadKey, saveKey, clearKey, errorMessage, isAuthError } from './api'

const Context = React.createContext()

const GENERIC_ERROR = 'Something went wrong. If the problem persists please see an ATG staff member.'

class Provider extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      key: loadKey(),
      loading: false,
      errors: '',
      student: null,
      studentExists: false,
      isStudent: true,
      submitted: false,

      clearIncidents: this.clearIncidents,
      createIncident: this.createIncident,
      handleAuth: this.handleAuth,
      handleSubmit: this.handleSubmit,
      resetForm: this.resetForm,
      setIsStudent: this.setIsStudent,
      updateIncident: this.updateIncident,
    }
  }

  /**
   * Single place every failed request lands. A rejected key sends the kiosk back
   * to the unlock screen instead of showing an unrelated "email not found".
   */
  handleApiError = (error, fallback) => {
    if (isAuthError(error)) {
      clearKey()
      this.setState({
        key: '',
        loading: false,
        student: null,
        studentExists: false,
        submitted: false,
        errors: 'This kiosk needs to be unlocked again.',
      })
      return
    }
    this.setState({ loading: false, errors: errorMessage(error, fallback) })
  }

  handleAuth = async (event) => {
    event.preventDefault()
    const password = event.target.password.value
    this.setState({ loading: true, errors: '' })

    try {
      const { data } = await api.get('signin', {
        skipAuth: true,
        headers: { Authorization: basicAuth(password) },
      })
      saveKey(data.key)
      this.setState({ key: data.key, loading: false, errors: '' })
    } catch (error) {
      // Always resets loading - the old version left the spinner up forever.
      this.setState({
        key: '',
        loading: false,
        errors: errorMessage(error, 'Incorrect password. Please try again.'),
      })
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault()
    const studentEmail = event.target.studentEmail.value.trim()
    if (!studentEmail) return

    this.setState({ loading: true, errors: '' })
    try {
      const { data } = await api.get(`samanage/users/find/${encodeURIComponent(studentEmail)}`)
      this.setState({ student: data, studentExists: true, loading: false, errors: '' })
    } catch (error) {
      this.handleApiError(
        error,
        'Your email was not found on our system, please try again... If the problem persists please see an ATG staff member.'
      )
    }
  }

  createIncident = async (event) => {
    event.preventDefault()
    const { email, name } = this.state.student
    const category = event.target.category.value
    const description = event.target.description.value.trim()

    if (!description) {
      this.setState({ errors: 'Please describe the problem before submitting.' })
      return
    }

    this.setState({ loading: true, errors: '' })
    try {
      // A JSON body, so "&" and "#" in a description can no longer truncate it.
      await api.post('samanage/incidents', {
        email,
        student_name: name,
        category,
        description,
      })
      this.setState({ studentExists: false, loading: false, errors: '', submitted: true })
    } catch (error) {
      this.handleApiError(error, `Could not create a new incident. ${GENERIC_ERROR}`)
    }
  }

  updateIncident = async (incidentId) => {
    const { student } = this.state
    const incident = (student?.incidents || []).find((item) => item.id === incidentId)
    if (!incident) return

    this.setState({ loading: true, errors: '' })
    try {
      await api.post(`samanage/incidents/${encodeURIComponent(incidentId)}/checkin`, {
        student_name: student.name,
        // Optional chaining matters: incidents without a category used to throw
        // here and the check-in silently never fired.
        category: incident.category?.name || '',
        description: incident.description || '',
      })
      this.setState({ loading: false, errors: '', submitted: true })
    } catch (error) {
      this.handleApiError(error, `Could not update the incident. ${GENERIC_ERROR}`)
    }
  }

  clearIncidents = (event) => {
    event.preventDefault()
    this.setState((state) => ({
      errors: '',
      student: { ...state.student, incidents: [] },
    }))
  }

  resetForm = (event) => {
    event.preventDefault()
    this.setState({
      submitted: false,
      student: null,
      studentExists: false,
      isStudent: true,
      errors: '',
      loading: false,
    })
  }

  setIsStudent = (event, isStudent) => {
    event.preventDefault()
    this.setState({ isStudent, errors: '' })
  }

  render() {
    return <Context.Provider value={this.state}>{this.props.children}</Context.Provider>
  }
}

export default Provider
export const Consumer = Context.Consumer
